import {
  appPath,
  viewRouteOf,
  workAreaReferenceOf,
  workAreaRoutePath,
} from '@featit/shared/addressing/paths';

/**
 * Where the shell's search bar is, and what it reads (F-0176, F-0743 §4).
 *
 * F-0176's switch to the work items at the third character is gone (D-0431, VSB-32): the bar never
 * moves the reader by itself, and **Show all in Work items** goes there. What stays is which path is
 * Items and where a work area's Items is.
 */

/**
 * Whether this path is the work-items view — including an item opened inside it, which is still
 * the results view with a panel over it rather than somewhere else.
 *
 * ⚠️ Read from the path alone, never by comparing against the work area's own results path. That
 * comparison looks equivalent and is not: the results path is built from the *selected* work area,
 * which is briefly unset while a route change is in flight, and for those renders every results
 * path stops matching. The return trip was being retired mid-transition for exactly that reason —
 * the reader emptied the field and stayed on the results view, because by then nothing remembered
 * where they had come from.
 */
export function isResultsPath(pathname: string): boolean {
  const route = viewRouteOf(pathname);
  if (route === null) return false;
  // `…/items/new` is the creation form, not a result.
  if (/^\/items\/new\/?$/.test(route)) return false;
  return /^\/items(\/|$)/.test(route);
}

/**
 * Where this reader's results live — the work area in the **address**, not the one in the store.
 *
 * ⚠️ The two disagree more often than they look like they would. Selection is set by the arriving
 * view, so on any render where the route has moved and selection has not yet caught up, reading the
 * store answers with the previous work area — or, on a cold load, with none at all, which builds the
 * unscoped `/-/items` and silently drops the reader out of the work area they were reading. That is
 * the same route-versus-selection hazard `Layout` already carries a warning about (I-0204); here it
 * would send the reader somewhere they never asked to go, mid-keystroke. The path is authoritative
 * whenever it names a work area, and the store is only the fallback for the unscoped routes.
 */
export function resultsPathFor(pathname: string, selectedSlug?: string | null): string {
  const routeRef = workAreaReferenceOf(pathname);
  if (routeRef) return workAreaRoutePath(routeRef, '/items');
  return selectedSlug ? workAreaRoutePath(selectedSlug, '/items') : appPath('/items');
}

/**
 * Whether the shell offers search on this view at all.
 *
 * The four views the field serves, and nowhere else: offering a work-item search on Settings or
 * Agents would be offering to do something those screens cannot do. Because all four answer `true`,
 * the field is never unmounted while the reader moves between them — which is what makes the switch
 * seamless rather than merely quick.
 */
export function showsGlobalSearch(
  pathname: string,
  /**
   * AGM-51: the address is a guest's work area. Search opens Work items, a member read, so a guest
   * is not offered it there; what is shared with them is their pages and *Shared with you*.
   */
  { guest = false }: { guest?: boolean } = {},
): boolean {
  if (guest) return false;
  const route = viewRouteOf(pathname);
  // Home: the app's, and a work area's own address.
  if (route === '/home' || (route === '' && workAreaReferenceOf(pathname))) return true;
  if (route === null) return false;
  if (/^\/items\/new\/?$/.test(route)) return false;
  return /^\/(items|activity|roadmap)(\/|$)/.test(route);
}
